import apiClient from './api';

export type AnalyticsPlatform =
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'linkedin'
  | 'facebook'
  | 'pinterest'
  | 'x'
  | 'all';

export type AnalyticsMetricType =
  | 'views'
  | 'subscribers'
  | 'followers'
  | 'reach'
  | 'impressions'
  | 'ctr'
  | 'watchTime'
  | 'averageDuration'
  | 'retention'
  | 'likes'
  | 'comments'
  | 'shares'
  | 'bookmarks'
  | 'downloads'
  | 'conversions'
  | 'revenue'
  | 'rpm'
  | 'cpm'
  | 'affiliateSales';

export type DateBucket = 'day' | 'week' | 'month' | 'year';
export type AnalyticsPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
export type ComparisonType = 'platform' | 'content' | 'campaign' | 'creator';
export type AudienceDimensionType =
  | 'region'
  | 'language'
  | 'ageGroup'
  | 'gender'
  | 'device'
  | 'topic'
  | 'hashtag'
  | 'interest';

export interface AnalyticsFilters {
  period?: AnalyticsPeriod;
  start?: string;
  end?: string;
  platforms?: AnalyticsPlatform[];
  contentId?: string;
  campaignId?: string;
  creatorId?: string;
  region?: string;
  language?: string;
  topic?: string;
  hashtag?: string;
}

export interface MetricValue {
  value: number;
  change: number | null;
}

export interface OverviewResponse {
  success: boolean;
  data: {
    period: { start: string; end: string };
    previousPeriod: { start: string; end: string };
    totals: Record<AnalyticsMetricType, MetricValue>;
    topPlatforms: Array<{ platform: AnalyticsPlatform; views: number; engagement: number }>;
    topContent: Array<{
      contentId: string;
      contentTitle: string;
      platform: AnalyticsPlatform;
      views: number;
      engagement: number;
    }>;
  };
}

export interface TimeSeriesPoint {
  bucket: string;
  [metric: string]: string | number | null;
}

export interface TimeSeriesResponse {
  success: boolean;
  data: TimeSeriesPoint[];
}

export interface ComparisonItem {
  key: string;
  label: string;
  platform?: AnalyticsPlatform;
  views: number;
  watchTime: number;
  likes: number;
  comments: number;
  shares: number;
  ctr: number;
  engagement: number;
}

export interface ComparisonResponse {
  success: boolean;
  data: ComparisonItem[];
}

export interface AudienceDimension {
  dimension: AudienceDimensionType;
  value: string;
  views: number;
  watchTime: number;
  engagement: number;
}

export interface AudienceResponse {
  success: boolean;
  data: AudienceDimension[];
}

export interface AIInsight {
  type: string;
  title: string;
  description: string;
  value?: string | number;
}

export interface InsightsResponse {
  success: boolean;
  data: AIInsight[];
}

function buildParams(filters: AnalyticsFilters) {
  const params: Record<string, string> = {};
  if (filters.period) params.period = filters.period;
  if (filters.start) params.start = filters.start;
  if (filters.end) params.end = filters.end;
  if (filters.platforms?.length) params.platforms = filters.platforms.join(',');
  if (filters.contentId) params.contentId = filters.contentId;
  if (filters.campaignId) params.campaignId = filters.campaignId;
  if (filters.creatorId) params.creatorId = filters.creatorId;
  if (filters.region) params.region = filters.region;
  if (filters.language) params.language = filters.language;
  if (filters.topic) params.topic = filters.topic;
  if (filters.hashtag) params.hashtag = filters.hashtag;
  return params;
}

export const analyticsApi = {
  getOverview: async (filters: AnalyticsFilters = {}): Promise<OverviewResponse> => {
    const response = await apiClient.get('/analytics/overview', { params: buildParams(filters) });
    return response.data;
  },
  getTimeSeries: async (
    metrics: AnalyticsMetricType[],
    bucket: DateBucket,
    filters: AnalyticsFilters = {}
  ): Promise<TimeSeriesResponse> => {
    const response = await apiClient.get('/analytics/time-series', {
      params: { ...buildParams(filters), metrics: metrics.join(','), bucket },
    });
    return response.data;
  },
  getComparison: async (
    type: ComparisonType,
    filters: AnalyticsFilters = {}
  ): Promise<ComparisonResponse> => {
    const response = await apiClient.get('/analytics/comparison', {
      params: { ...buildParams(filters), type },
    });
    return response.data;
  },
  getAudience: async (
    dimension: AudienceDimensionType,
    filters: AnalyticsFilters = {}
  ): Promise<AudienceResponse> => {
    const response = await apiClient.get('/analytics/audience', {
      params: { ...buildParams(filters), dimension },
    });
    return response.data;
  },
  getInsights: async (filters: AnalyticsFilters = {}): Promise<InsightsResponse> => {
    const response = await apiClient.get('/analytics/insights', { params: buildParams(filters) });
    return response.data;
  },
  syncYouTube: async (startDate?: string, endDate?: string): Promise<{ success: boolean; synced: number; message: string; accounts: Array<{ channelId: string; channelTitle: string; rows: number; error?: string }> }> => {
    const response = await apiClient.post('/analytics/sync', { startDate, endDate });
    return response.data;
  },
  seedDemo: async (days?: number): Promise<{ success: boolean; created: number }> => {
    const response = await apiClient.post('/analytics/seed', { days });
    return response.data;
  },
  clearDemo: async (): Promise<{ success: boolean; deleted: number }> => {
    const response = await apiClient.post('/analytics/clear-demo');
    return response.data;
  },
};
